import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { knowledgeBaseApi } from 'api/knowledgeBase';
import { Box, BoxSection } from 'components/_new/Box';
import { Button } from 'components/_new/Button';
import { usePanel } from 'contexts/panelContext';
import { useAuth } from 'contexts/authContext';
import { slugify } from 'utils/helpers';
import { useKnowledgeBase } from './knowledgeBaseContext';
import { FavoriteIcon } from './FavoriteIcon';
import { ReadedIcon } from './ReadedIcon';
import { ToreadIcon } from './ToreadIcon';
import { FlaggedArticlesSkeleton } from './skeletons/FlaggedArticlesSkeleton';

export const FlaggedArticles = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { currentUser } = useAuth();
	const { currentApplicationData } = usePanel();
	const { knowledgeBaseLinkPrefix } = useKnowledgeBase();

	const { data, isLoading } = useQuery(
		[
			'KNOWLEDGE_BASE_FLAGGED',
			{
				applicationId: Number(currentApplicationData?.id),
				languageCode: currentUser?.language,
				isProfessionalViewEnabled: currentUser?.isProfessionalViewEnabled,
			},
		],
		() => knowledgeBaseApi.application.getFlagged(Number(currentApplicationData?.id)),
		{
			enabled: true,
		}
	);

	if (isLoading) {
		return <FlaggedArticlesSkeleton />;
	}

	return (
		<div className="flex flex-row flex-wrap gap-2">
			<Box className="flex-1" style={{ minWidth: 200 }}>
				<BoxSection
					className="flex-1"
					header={() => (
						<Link
							to={{ pathname: `${knowledgeBaseLinkPrefix}/favorites` }}
							className="inline-flex flex-row gap-2 align-items-center"
						>
							<FavoriteIcon checked={false} /> {t('knowledgeBase.favorite.favorite')}{' '}
							{` (${data?.favorites.count || 0})`}
						</Link>
					)}
				>
					{!data?.favorites.count && <span className="text-muted">{t('knowledgeBase.favorite.hint')}</span>}
					{data?.favorites?.items.map(({ articleId, slug, title }) => {
						const pathname = `${knowledgeBaseLinkPrefix}/article/${articleId}${
							slug ? `/${slugify(slug)}` : ''
						}`;
						return (
							<Link key={articleId} to={{ pathname }} className="text-primary">
								{title}
							</Link>
						);
					})}
				</BoxSection>
				{(data?.favorites?.count || 0) > 5 && (
					<BoxSection>
						<Button
							label={t('actions.showMore')}
							onClick={() => {
								navigate({
									pathname: `${knowledgeBaseLinkPrefix}/favorites`,
								});
							}}
							variant="gray-primary"
						/>
					</BoxSection>
				)}
			</Box>
			<Box className="flex-1" style={{ minWidth: 200 }}>
				<BoxSection
					className="flex-1"
					header={() => (
						<Link
							to={{ pathname: `${knowledgeBaseLinkPrefix}/readed` }}
							className="inline-flex flex-row gap-2 align-items-center"
						>
							<ReadedIcon checked={false} /> {t('knowledgeBase.readed.readed')}
							{` (${data?.readed.count || 0})`}
						</Link>
					)}
				>
					{!data?.readed.count && <span className="text-muted">{t('knowledgeBase.readed.hint')}</span>}
					{data?.readed?.items.map(({ articleId, slug, title }) => {
						const pathname = `${knowledgeBaseLinkPrefix}/article/${articleId}${
							slug ? `/${slugify(slug)}` : ''
						}`;
						return (
							<Link key={articleId} to={{ pathname }} className="text-primary">
								{title}
							</Link>
						);
					})}
				</BoxSection>
				{(data?.readed?.count || 0) > 5 && (
					<BoxSection>
						<Button
							label={t('actions.showMore')}
							onClick={() => {
								navigate({
									pathname: `${knowledgeBaseLinkPrefix}/readed`,
								});
							}}
							variant="gray-primary"
						/>
					</BoxSection>
				)}
			</Box>
			<Box className="flex-1" style={{ minWidth: 200 }}>
				<BoxSection
					className="flex-1"
					header={() => (
						<Link
							to={{ pathname: `${knowledgeBaseLinkPrefix}/toread` }}
							className="inline-flex flex-row gap-2 align-items-center"
						>
							<ToreadIcon checked={false} /> {t('knowledgeBase.toRead.toRead')}
							{` (${data?.toRead.count || 0})`}
						</Link>
					)}
				>
					{!data?.toRead.count && <span className="text-muted">{t('knowledgeBase.toRead.hint')}</span>}
					{data?.toRead?.items.map(({ articleId, slug, title }) => {
						const pathname = `${knowledgeBaseLinkPrefix}/article/${articleId}${
							slug ? `/${slugify(slug)}` : ''
						}`;
						return (
							<Link key={articleId} to={{ pathname }} className="text-primary">
								{title}
							</Link>
						);
					})}
				</BoxSection>
				{(data?.toRead?.count || 0) > 5 && (
					<BoxSection>
						<Button
							label={t('actions.showMore')}
							onClick={() => {
								navigate({
									pathname: `${knowledgeBaseLinkPrefix}/toread`,
								});
							}}
							variant="gray-primary"
						/>
					</BoxSection>
				)}
			</Box>
		</div>
	);
};
